import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ListChecks, Clock, Car, Users, WarningOctagon, Funnel, ArrowClockwise } from "@phosphor-icons/react";
import { GuestTags } from "@/components/GuestTags";

function StatCard({ label, value, icon: Icon, color = "#007AFF" }) {
  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
          <Icon size={20} weight="bold" style={{ color }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-[#737373]">{label}</p>
          <p className="font-heading text-2xl font-bold text-white">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function RankedTable({ title, items, renderRow }) {
  return (
    <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={2} className="text-center text-[#737373] text-sm py-4">No data</TableCell></TableRow>
            ) : (
              items.map(renderRow)
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function AnalyticsTab() {
  const [summary, setSummary] = useState(null);
  const [topCars, setTopCars] = useState([]);
  const [topRooms, setTopRooms] = useState([]);
  const [highRisk, setHighRisk] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("23:59");
  const [filtered, setFiltered] = useState(false);

  const buildParams = useCallback(() => {
    if (!startDate || !endDate) return "";
    const s = new Date(`${startDate}T${startTime || "00:00"}:00`).toISOString();
    const e = new Date(`${endDate}T${endTime || "23:59"}:59`).toISOString();
    return `&start_date=${s}&end_date=${e}`;
  }, [startDate, startTime, endDate, endTime]);

  const loadData = useCallback(async (dateParams = "") => {
    try {
      const [sumRes, carsRes, roomsRes, riskRes] = await Promise.all([
        api.get(`/analytics/summary?_=1${dateParams}`),
        api.get(`/analytics/top-cars?limit=10${dateParams}`),
        api.get(`/analytics/top-rooms?limit=10${dateParams}`),
        api.get(`/analytics/high-risk?limit=10${dateParams}`),
      ]);
      setSummary(sumRes.data);
      setTopCars(carsRes.data);
      setTopRooms(roomsRes.data);
      setHighRisk(riskRes.data);
    } catch (err) {
      console.error("Failed to load analytics:", err);
    }
  }, []);

  // Initial load (no filter)
  useEffect(() => { loadData(); }, [loadData]);

  const handleApplyFilter = () => {
    const params = buildParams();
    if (params) {
      loadData(params);
      setFiltered(true);
    }
  };

  const handleClearFilter = () => {
    setStartDate("");
    setStartTime("00:00");
    setEndDate("");
    setEndTime("23:59");
    setFiltered(false);
    loadData();
  };

  if (!summary) return <div className="py-8 text-center text-[#737373]">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      {/* Date/Time Range Filter */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Funnel size={16} weight="bold" className="text-[#007AFF]" />
            Date / Time Range
            {filtered && <span className="text-[10px] text-[#32D74B] font-normal uppercase tracking-wider ml-2">Filtered</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data h-8 text-xs" data-testid="analytics-start-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-[#737373]">Start Time</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data h-8 text-xs" data-testid="analytics-start-time" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-[#737373]">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data h-8 text-xs" data-testid="analytics-end-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider text-[#737373]">End Time</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm font-mono-data h-8 text-xs" data-testid="analytics-end-time" />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={handleApplyFilter} disabled={!startDate || !endDate}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="analytics-apply-filter">
              <Funnel size={14} className="mr-1" /> Apply Filter
            </Button>
            {filtered && (
              <Button size="sm" variant="outline" onClick={handleClearFilter}
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs" data-testid="analytics-clear-filter">
                <ArrowClockwise size={14} className="mr-1" /> Clear Filter
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Total Validations" value={summary.total_validations} icon={ListChecks} />
        <StatCard label="Today" value={summary.today_validations} icon={Clock} color="#32D74B" />
        <StatCard label="Flagged" value={summary.flagged_count} icon={WarningOctagon} color="#FF3B30" />
        <StatCard label="Cars Tracked" value={summary.total_cars} icon={Car} color="#FF9F0A" />
        <StatCard label="Users" value={summary.total_users} icon={Users} color="#007AFF" />
      </div>

      {/* Ranked Tables */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <RankedTable
          title="Most Validated Cars"
          items={topCars}
          renderRow={(c) => (
            <TableRow key={c.car_plate} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
              <TableCell className="font-mono-data text-sm text-white tracking-wider">{c.car_plate}</TableCell>
              <TableCell className="font-mono-data text-sm text-[#007AFF] text-right">{c.total_visits}</TableCell>
            </TableRow>
          )}
        />
        <RankedTable
          title="Most Used Rooms"
          items={topRooms}
          renderRow={(r) => (
            <TableRow key={r.room_number} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
              <TableCell className="font-mono-data text-sm text-white">Room {r.room_number}<GuestTags vip={r.guest_vip} rateCode={r.guest_rate_code} /></TableCell>
              <TableCell className="font-mono-data text-sm text-[#007AFF] text-right">{r.count}</TableCell>
            </TableRow>
          )}
        />
        <RankedTable
          title="Flagged Vehicles"
          items={highRisk}
          renderRow={(v) => (
            <TableRow key={v.car_plate} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
              <TableCell className="font-mono-data text-sm text-white tracking-wider">{v.car_plate}</TableCell>
              <TableCell className="font-mono-data text-xs text-[#FF3B30] text-right">AVG {v.avg_score}</TableCell>
            </TableRow>
          )}
        />
      </div>
    </div>
  );
}
